import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { RotateCcw, ZoomIn, ZoomOut } from 'lucide-react';
import {
  baseCropSize,
  clampView,
  CROP_SHAPES,
  cropRect,
  initialView,
  isAnimatedImage,
  MAX_ZOOM,
  MIN_ZOOM,
  outputSize,
  panBy,
  zoomTo,
  type CropKind,
  type CropView,
  type Size,
} from '../lib/crop';
import { Button, IconButton, Modal } from './ui';

const TITLES: Record<CropKind, string> = { avatar: 'Crop your avatar', icon: 'Crop the space icon', banner: 'Crop the banner' };
/** Space between the frame and the edge of the stage, so some of the image outside the frame shows. */
const PAD = 24;
const KEY_STEP = 10;

/**
 * Hands picked images through the cropper before `onFile`. Animated images skip it and upload as they are,
 * since drawing them to a canvas would keep only the first frame.
 */
export function useImageCropper(kind: CropKind, onFile: (file: File) => void) {
  const [pending, setPending] = useState<File | null>(null);
  const pick = useCallback(
    (file: File) => {
      void file
        .slice(0, 256 * 1024)
        .arrayBuffer()
        .then((buf) => (isAnimatedImage(new Uint8Array(buf)) ? onFile(file) : setPending(file)))
        .catch(() => setPending(file));
    },
    [onFile],
  );
  const dialog = pending ? (
    <ImageCropDialog
      key={`${pending.name}:${pending.size}:${pending.lastModified}`}
      file={pending}
      kind={kind}
      onCancel={() => setPending(null)}
      onApply={(f) => {
        setPending(null);
        onFile(f);
      }}
    />
  ) : null;
  return { pick, dialog };
}

function ImageCropDialog({ file, kind, onCancel, onApply }: { file: File; kind: CropKind; onCancel: () => void; onApply: (file: File) => void }) {
  const shape = CROP_SHAPES[kind];
  // Made in an effect rather than a memo so StrictMode's remount doesn't leave a revoked URL behind.
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const next = URL.createObjectURL(file);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file]);

  const imgRef = useRef<HTMLImageElement>(null);
  // The dialog content mounts after this component (it is portalled), so the stage is tracked as state.
  const [stageEl, setStageEl] = useState<HTMLDivElement | null>(null);
  const [image, setImage] = useState<Size | null>(null);
  const [failed, setFailed] = useState(false);
  const [view, setView] = useState<CropView>({ zoom: 1, cx: 0, cy: 0 });
  const [stage, setStage] = useState<Size>({ width: 0, height: 0 });
  const [busy, setBusy] = useState(false);

  useLayoutEffect(() => {
    if (!stageEl) return;
    const measure = () => setStage({ width: stageEl.clientWidth, height: stageEl.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(stageEl);
    return () => ro.disconnect();
  }, [stageEl]);

  const frameWidth = Math.max(0, Math.min(stage.width - PAD * 2, (stage.height - PAD * 2) * shape.aspect));
  const frame = { width: frameWidth, height: frameWidth / shape.aspect, left: (stage.width - frameWidth) / 2, top: (stage.height - frameWidth / shape.aspect) / 2 };

  // Gestures read the latest values through refs, so handlers don't need rebinding on every change.
  const live = useRef({ image, frame, aspect: shape.aspect });
  live.current = { image, frame, aspect: shape.aspect };
  const update = useCallback((fn: (img: Size, v: CropView) => CropView) => {
    const img = live.current.image;
    if (img) setView((v) => fn(img, v));
  }, []);

  /** A point on screen as a fraction of the frame, relative to its centre. */
  const focusAt = useCallback(
    (clientX: number, clientY: number) => {
      const rect = stageEl!.getBoundingClientRect();
      const f = live.current.frame;
      return { x: (clientX - rect.left - f.left - f.width / 2) / f.width, y: (clientY - rect.top - f.top - f.height / 2) / f.height };
    },
    [stageEl],
  );

  // Wheel zoom needs a non-passive listener to stop the dialog scrolling.
  useEffect(() => {
    const el = stageEl;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 400 : 1);
      const focus = focusAt(e.clientX, e.clientY);
      update((img, v) => zoomTo(img, live.current.aspect, v, v.zoom * Math.exp(-delta * 0.0015), focus));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [stageEl, focusAt, update]);

  const pointers = useRef(new Map<number, { x: number; y: number }>());
  function onPointerDown(e: PointerEvent<HTMLDivElement>) {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // Not an active pointer (e.g. a synthetic event); tracking still works without capture.
    }
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
  }
  function onPointerMove(e: PointerEvent<HTMLDivElement>) {
    const map = pointers.current;
    const prev = map.get(e.pointerId);
    if (!prev) return;
    const others = [...map.entries()].filter(([id]) => id !== e.pointerId).map(([, p]) => p);
    map.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const { aspect, frame: f } = live.current;
    if (others.length === 0) {
      const dx = e.clientX - prev.x;
      const dy = e.clientY - prev.y;
      update((img, v) => panBy(img, aspect, v, dx, dy, f.width));
      return;
    }
    // Pinch: zoom by the change in finger spread around their midpoint, and follow the midpoint as it moves.
    const other = others[0]!;
    const before = Math.hypot(prev.x - other.x, prev.y - other.y);
    const after = Math.hypot(e.clientX - other.x, e.clientY - other.y);
    const mid = { x: (e.clientX + other.x) / 2, y: (e.clientY + other.y) / 2 };
    const dx = mid.x - (prev.x + other.x) / 2;
    const dy = mid.y - (prev.y + other.y) / 2;
    const focus = focusAt(mid.x, mid.y);
    update((img, v) => {
      const moved = panBy(img, aspect, v, dx, dy, f.width);
      return before > 0 ? zoomTo(img, aspect, moved, moved.zoom * (after / before), focus) : moved;
    });
  }
  function onPointerEnd(e: PointerEvent<HTMLDivElement>) {
    pointers.current.delete(e.pointerId);
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const step = e.shiftKey ? KEY_STEP * 4 : KEY_STEP;
    // The image follows the arrow, as if dragged that way.
    const moves: Record<string, [number, number]> = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    const move = moves[e.key];
    if (move) {
      e.preventDefault();
      update((img, v) => panBy(img, shape.aspect, v, move[0], move[1], frame.width));
    } else if (e.key === '+' || e.key === '=') {
      e.preventDefault();
      update((img, v) => zoomTo(img, shape.aspect, v, v.zoom * 1.1));
    } else if (e.key === '-' || e.key === '_') {
      e.preventDefault();
      update((img, v) => zoomTo(img, shape.aspect, v, v.zoom / 1.1));
    } else if (e.key === '0') {
      e.preventDefault();
      update((img) => initialView(img));
    }
  }

  async function apply() {
    const img = imgRef.current;
    if (!image || !img) return;
    setBusy(true);
    try {
      const rect = cropRect(image, shape.aspect, view);
      const out = outputSize(rect, shape);
      const canvas = document.createElement('canvas');
      canvas.width = out.width;
      canvas.height = out.height;
      const ctx = canvas.getContext('2d')!;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, rect.x, rect.y, rect.width, rect.height, 0, 0, out.width, out.height);
      // WebP keeps transparency and stays small; browsers that can't encode it hand back a PNG instead.
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/webp', 0.92));
      if (!blob) throw new Error('Could not crop the image.');
      const ext = blob.type === 'image/webp' ? 'webp' : 'png';
      onApply(new File([blob], `${file.name.replace(/\.[^.]*$/, '') || kind}.${ext}`, { type: blob.type || 'image/png' }));
    } catch {
      setFailed(true);
      setBusy(false);
    }
  }

  const v = image ? clampView(image, shape.aspect, view) : view;
  const scale = image && frame.width > 0 ? frame.width / (baseCropSize(image, shape.aspect).width / v.zoom) : 0;
  const isInitial = !image || (v.zoom === 1 && Math.abs(v.cx - image.width / 2) < 0.5 && Math.abs(v.cy - image.height / 2) < 0.5);
  const zoomBy = (factor: number) => update((img, cur) => zoomTo(img, shape.aspect, cur, cur.zoom * factor));

  return (
    <Modal
      open
      onClose={onCancel}
      title={TITLES[kind]}
      description="Drag to move it. Zoom with the slider, the scroll wheel or a pinch."
      size={kind === 'banner' ? 'lg' : 'md'}
      footer={
        <>
          <Button variant="ghost" icon={RotateCcw} onClick={() => image && setView(initialView(image))} disabled={isInitial || busy}>
            Reset
          </Button>
          <div className="flex-1" />
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          {failed ? (
            <Button onClick={() => onApply(file)}>Upload without cropping</Button>
          ) : (
            <Button onClick={() => void apply()} loading={busy} disabled={!image}>
              Apply
            </Button>
          )}
        </>
      }
    >
      <div
        ref={setStageEl}
        tabIndex={0}
        role="group"
        aria-label="Crop area. Arrow keys move the image, plus and minus zoom, 0 resets."
        className={`relative touch-none select-none overflow-hidden rounded-lg bg-sidebar outline-none focus-visible:ring-2 focus-visible:ring-accent ${kind === 'banner' ? 'h-64' : 'h-72'} ${image ? 'cursor-grab active:cursor-grabbing' : ''}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        onKeyDown={onKeyDown}
      >
        <img
          ref={imgRef}
          src={url ?? undefined}
          alt=""
          draggable={false}
          onLoad={(e) => {
            const size = { width: e.currentTarget.naturalWidth, height: e.currentTarget.naturalHeight };
            if (!size.width || !size.height) return setFailed(true);
            setImage(size);
            setView(initialView(size));
          }}
          onError={() => setFailed(true)}
          className="pointer-events-none absolute left-0 top-0 max-w-none origin-top-left"
          style={
            image && scale
              ? {
                  width: image.width * scale,
                  height: image.height * scale,
                  transform: `translate(${frame.left + frame.width / 2 - v.cx * scale}px, ${frame.top + frame.height / 2 - v.cy * scale}px)`,
                }
              : { visibility: 'hidden' }
          }
        />
        {image && frame.width > 0 && (
          <div
            aria-hidden
            className="pointer-events-none absolute outline-2 outline-white/80"
            style={{ left: frame.left, top: frame.top, width: frame.width, height: frame.height, borderRadius: shape.radius, boxShadow: '0 0 0 9999px rgb(0 0 0 / 0.6)' }}
          />
        )}
        {failed && !image && <p className="absolute inset-0 flex items-center justify-center p-6 text-center text-sm text-muted">This image can't be shown for cropping here.</p>}
      </div>
      {failed && image && <p className="mt-3 text-sm text-danger">Cropping failed in this browser. You can upload the image as it is instead.</p>}
      <div className="mt-4 flex items-center gap-2">
        <IconButton icon={ZoomOut} label="Zoom out" onClick={() => zoomBy(1 / 1.25)} disabled={!image || v.zoom <= MIN_ZOOM} />
        <input
          type="range"
          min={MIN_ZOOM}
          max={MAX_ZOOM}
          step={0.01}
          value={v.zoom}
          disabled={!image}
          onChange={(e) => update((img, cur) => zoomTo(img, shape.aspect, cur, Number(e.target.value)))}
          className="min-w-0 flex-1 accent-[var(--p-accent)]"
          aria-label="Zoom"
          aria-valuetext={`${Math.round(v.zoom * 100)}%`}
        />
        <IconButton icon={ZoomIn} label="Zoom in" onClick={() => zoomBy(1.25)} disabled={!image || v.zoom >= MAX_ZOOM} />
      </div>
    </Modal>
  );
}
